import { FC, useEffect, useState } from 'react';
import { Avatar, Button, Col, Divider, Dropdown, Row } from 'antd';
import { capitalCase } from 'change-case';
import {
    TbDots,
    TbMailX,
    TbPyramid,
    TbUserCancel,
    TbUserCheck,
    TbUserHexagon,
    TbUserPause,
    TbUserPentagon,
    TbUserSquare,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEntitlements } from '@/hooks/useEntitlements';
import { DropdownMenuPanel, VirtualizedList as List, MiddleDot, SmallTag } from '@/shared';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { useMemberStore } from '@/stores';
import { Member, MemberPermissionType, MemberStatusType } from '@/types';
import { ellipsisStyling, formatDate } from '@/utilities';

interface MembersListItemProps {
    member: Member;
    status?: MemberStatusType;
}

const MembersListItem: FC<MembersListItemProps> = ({ member }) => {
    const [loading, setLoading] = useState(true);
    const [permission, setPermission] = useState(member?.permission);

    const { hasFeature } = useEntitlements();
    const {
        selectedMember: currentMember,
        updateMember,
        updateMemberPermission,
        deleteMember: deleteMemberFromStore,
    } = useMemberStore();

    const activeMember = currentMember;

    const { token } = useAntDesignTheme();

    const isOwner = member?.permission === MemberPermissionType.OWNER;

    useEffect(() => {
        const timer = setTimeout(() => setLoading(false), 300);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        setPermission(member?.permission);
    }, [member?.permission]);

    const permissions = [
        {
            key: MemberPermissionType.GUEST,
            icon: <TbUserSquare size='1rem' style={{ verticalAlign: '-0.2rem', marginRight: '0.325rem' }} />,
            label: 'Guest',
            description: 'Can view select workspace resources.',
            disabled: !hasFeature('rbac'),
        },
        {
            key: MemberPermissionType.MEMBER,
            icon: <TbUserHexagon size='1rem' style={{ verticalAlign: '-0.2rem', marginRight: '0.325rem' }} />,
            label: 'Member',
            description: "Can edit workspace resources they're invited to.",
            disabled: false,
        },
        {
            key: MemberPermissionType.ADMIN,
            icon: <TbUserPentagon size='1rem' style={{ verticalAlign: '-0.2rem', marginRight: '0.325rem' }} />,
            label: 'Admin',
            description: 'Full visibility and access to the workspace.',
            disabled: !hasFeature('rbac'),
        },
    ];

    const updateMemberStatus = async (status: MemberStatusType, toastMsg: string, icon: JSX.Element) => {
        try {
            await updateMember(member.id, { status });

            toast(toastMsg, {
                icon,
                description: `${member.user?.firstName} ${member.user?.lastName}`,
            });
        } catch {
            toast.error('Unable to Update Member');
        } finally {
            setLoading(false);
        }
    };

    const deleteMember = async (toastMsg: string, icon: JSX.Element) => {
        try {
            await deleteMemberFromStore(member.id);

            toast(toastMsg, {
                icon,
                description: `${member.user?.firstName} ${member.user?.lastName}`,
            });
        } catch {
            toast.error('Unable to Remove Member');
        } finally {
            setLoading(false);
        }
    };

    // Dropdown menu options based on member.status
    const getMenuItems = () => {
        switch (member.status) {
            case MemberStatusType.ACTIVE:
                return [
                    {
                        key: 'deactivate',
                        label: 'Deactivate Member',
                        icon: <TbUserPause size='1rem' />,
                        onClick: () =>
                            updateMemberStatus(
                                MemberStatusType.DEACTIVATED,
                                'Member Deactivated',
                                <TbUserPause size='1rem' />,
                            ),
                    },
                    {
                        key: 'block',
                        label: 'Block Member',
                        danger: true,
                        icon: <TbUserCancel size='1rem' />,
                        onClick: () =>
                            updateMemberStatus(
                                MemberStatusType.BLOCKED,
                                'Member Blocked',
                                <TbUserCancel size='1rem' />,
                            ),
                    },
                ];
            case MemberStatusType.DEACTIVATED:
                return [
                    {
                        key: 'reactivate',
                        label: 'Reactivate Member',
                        icon: <TbUserCheck size='1rem' />,
                        onClick: () =>
                            updateMemberStatus(
                                MemberStatusType.ACTIVE,
                                'Member Reactivated',
                                <TbUserCheck size='1rem' />,
                            ),
                    },
                    {
                        key: 'block',
                        label: 'Block Member',
                        danger: true,
                        icon: <TbUserCancel size='1rem' />,
                        onClick: () =>
                            updateMemberStatus(
                                MemberStatusType.BLOCKED,
                                'Member Blocked',
                                <TbUserCancel size='1rem' />,
                            ),
                    },
                ];
            case MemberStatusType.BLOCKED:
                return [
                    {
                        key: 'unblock',
                        label: 'Unblock User',
                        danger: true,
                        icon: <TbUserCancel size='1rem' />,
                        onClick: () => deleteMember('User Unblocked', <TbUserCancel size='1rem' />),
                    },
                ];
            case MemberStatusType.PENDING:
                return [
                    {
                        key: 'remove-invite',
                        label: 'Remove Invitation',
                        icon: <TbMailX size='1rem' />,
                        danger: true,
                        onClick: () => deleteMember('Invitation Removed', <TbMailX size='1rem' />),
                    },
                ];
            default:
                return [];
        }
    };

    const permissionControl = (
        <Dropdown
            trigger={['click']}
            disabled={isOwner || member.status !== MemberStatusType.ACTIVE}
            placement='bottomRight'
            popupRender={() => (
                <DropdownMenuPanel
                    {...{
                        selectedKeys: [permission as MemberPermissionType],
                        onClick: async (e) => {
                            try {
                                setLoading(true);
                                const nextPermission = e.key as MemberPermissionType;

                                setPermission(nextPermission);
                                await updateMemberPermission(member.id, nextPermission);

                                toast('Member Permission Changed', {
                                    icon: <TbPyramid size='1rem' />,
                                    description: `${member.user?.firstName} ${member.user?.lastName} → ${nextPermission}`,
                                });
                            } catch {
                                setPermission(member.permission);
                                toast.error('Unable to Update Member Permission');
                            } finally {
                                setLoading(false);
                            }
                        },
                        items: permissions.map((p) => ({
                            key: p.key,
                            label: (
                                <>
                                    {p.icon}
                                    {p.label}
                                    <br />
                                    <small style={{ color: token.colorTextTertiary }}>{p.description}</small>
                                </>
                            ),
                            disabled: p.disabled,
                        })),
                    }}
                />
            )}
        >
            <DropdownButton
                type='dashed'
                loading={loading}
                disabled={
                    isOwner ||
                    member.status !== MemberStatusType.ACTIVE ||
                    activeMember?.permission !== MemberPermissionType.OWNER
                }
            >
                <Row>
                    <Col span={20}>{permission}</Col>
                </Row>
            </DropdownButton>
        </Dropdown>
    );
    const memberActionsControl = (
        <Dropdown
            trigger={['click']}
            placement='bottomRight'
            overlayStyle={{ minWidth: 250 }}
            disabled={isOwner}
            popupRender={() => <DropdownMenuPanel {...{ items: getMenuItems() }} />}
        >
            <Button
                icon={<TbDots size='1rem' style={{ verticalAlign: '-0.2rem' }} />}
                type='text'
                disabled={isOwner || activeMember?.permission !== MemberPermissionType.OWNER}
            />
        </Dropdown>
    );

    return (
        <List.Item
            key={member?.id}
            extra={
                <span
                    data-member-permission-controls='true'
                    style={{ alignItems: 'center', display: 'inline-flex', flex: 'none' }}
                >
                    {permissionControl}
                    <Divider type='vertical' style={{ height: '1rem', marginInline: '0.5rem' }} />
                    {memberActionsControl}
                </span>
            }
        >
            <List.Item.Meta
                avatar={<Avatar src={member.user?.avatar} style={{ marginTop: '0.375rem' }} />}
                title={
                    <span style={{ fontWeight: 500 }}>
                        {member.user?.firstName} {member.user?.lastName}{' '}
                        {member.user?.username && (
                            <small
                                style={{ color: token.colorTextTertiary, marginLeft: '0.5rem', verticalAlign: '1px' }}
                            >
                                @{member.user?.username}
                            </small>
                        )}
                        {isOwner && (
                            <SmallTag
                                styles={{
                                    marginLeft: member.user?.username ? '0.75rem' : '0.25rem',
                                    borderColor: 'transparent',
                                }}
                                message={'Workspace Owner'}
                            />
                        )}
                    </span>
                }
                description={
                    <span style={ellipsisStyling}>
                        {member.user?.emailAddress}
                        <MiddleDot horizontalMargin='0.5rem' />
                        {`${capitalCase(member?.status as MemberStatusType)} ${member.createdAt ? formatDate(member?.createdAt) : '-'}`}
                    </span>
                }
            />
        </List.Item>
    );
};

export default MembersListItem;
